import { InfiniteLoadingSpinner } from 'components';
import { FC } from 'react';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { GreyStrokeInfoIcon } from 'assets/icons';

export interface ProfitabilitySummaryData {
  profitMargin: number;
  totalPaid: number;
  lineItemCost: number;
  taxesAndFees: number;
  expenses: number;
  compensation: number;
  profit: number;
  discount: number;
}

interface ProfitabilitySummaryProps {
  data?: ProfitabilitySummaryData;
  isPaidInvoice: boolean;
  isLoading: boolean;
}

const ProfitabilitySummary: FC<ProfitabilitySummaryProps> = ({ data, isPaidInvoice, isLoading = false }) => {
  const PROFITABILITY_SUMMARY_ITEMS = [
    { key: 'totalPaid', label: isPaidInvoice ? 'Total paid' : 'Job total', tooltip: '' },
    { key: 'lineItemCost', label: 'Line item costs', tooltip: '' },
    { key: 'taxesAndFees', label: 'Taxes and fees', tooltip: 'Sum of all the taxes + ScanPay fees' },
    { key: 'expenses', label: 'Expenses', tooltip: '' },
    { key: 'compensation', label: 'Referral and pay', tooltip: 'Sum of all commissions + external referral + tips' },
    { key: 'profit', label: +data?.profit > 0 ? 'Profit' : 'Loss', tooltip: '' }
  ] as const;

  const summaryItem = (item: typeof PROFITABILITY_SUMMARY_ITEMS[number], showOperator?: string) => (
    <>
      {showOperator && <div className="mr-2 self-start text-base font-medium text-gray-400">{showOperator}</div>}
      <div key={`column-${item.key}`} className="flex flex-col items-start">
        <div className="flex items-center">
          <div className="text-base font-semibold text-headingGray">
            {getAmountWithCurrency(Math.abs(data?.[item.key] || 0))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="mb-2 text-sbase text-[#878787]">{item.label}</div>
          {item.tooltip && (
            <TooltipWrapper
              tooltip={item.tooltip}
              customTooltipClassName={
                '!-translate-x-[93%] before:!left-[93%] border border-[#ACB5BD] !-top-[44px] ' +
                'before:!filter before:!drop-shadow-[0_1px_0_#ACB5BD] !px-3 !py-[10px]'
              }>
              <div className="cursor-pointer">
                <GreyStrokeInfoIcon className="mb-1 shrink-0" width={16} height={16} />
              </div>
            </TooltipWrapper>
          )}
        </div>
      </div>
    </>
  );

  const deductionItems = PROFITABILITY_SUMMARY_ITEMS.slice(1, -1);
  const [totalPaidItem] = PROFITABILITY_SUMMARY_ITEMS;
  const profitItem = PROFITABILITY_SUMMARY_ITEMS[PROFITABILITY_SUMMARY_ITEMS.length - 1];

  if (isLoading) {
    return (
      <div className="flex justify-center gap-x-12 rounded-md border border-borderGray py-[10px] px-3">
        <InfiniteLoadingSpinner />
      </div>
    );
  } else if (!data) {
    return null;
  }
  return (
    <div
      className="flex w-full justify-between gap-x-12 overflow-x-auto 
      rounded-md border border-borderGray py-[10px] px-3">
      <div className="mb-4 flex w-fit flex-col items-start">
        <div className={`text-2xl font-bold ${+data.profitMargin >= 0 ? 'text-successHighlight' : 'text-error'}`}>
          {data.profitMargin}%
        </div>
        <div className="mt-1 text-sbase text-[#878787]">Profit margin</div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-start gap-x-2 pt-[7px]">
          {summaryItem(totalPaidItem)}
          {deductionItems.map(item => summaryItem(item, '-'))}
          {summaryItem(profitItem, '=')}
        </div>
      </div>
    </div>
  );
};

export default ProfitabilitySummary;
